package com.fantasytenis.web.components.bracket

import androidx.compose.runtime.*
import com.fantasytenis.web.hooks.AutoSaveState
import com.fantasytenis.web.hooks.SaveStatus
import com.fantasytenis.web.hooks.formatRelativeTime
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Displays the current save status for bracket picks.
 *
 * Shows:
 * - "Saving..." with spinner during save
 * - "Saved ✓" with timestamp after successful save
 * - "Save failed" with retry info on error
 */
@Composable
fun SaveStatusIndicator(
    state: AutoSaveState,
    compact: Boolean = false
) {
    val (icon, text, color) = when (val status = state.status) {
        is SaveStatus.Idle -> Triple(null, if (compact) "" else "Ready", Colors.TextSecondary)
        is SaveStatus.Saving -> Triple("⟳", "Saving...", Colors.Primary)
        is SaveStatus.Saved -> Triple("✓", if (compact) "Saved" else "Saved", Colors.Success)
        is SaveStatus.Error -> Triple("⚠", if (compact) "Error" else "Save failed", Colors.Error)
    }

    if (text.isEmpty() && state.status is SaveStatus.Idle) {
        return // Don't render anything in idle state for compact mode
    }

    Div({
        classes("save-status-indicator")
        if (state.status is SaveStatus.Saving) classes("saving")
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(6.px)
            padding(4.px, 8.px)
            borderRadius(4.px)
            backgroundColor(
                when (state.status) {
                    is SaveStatus.Saving -> Colors.InfoLight
                    is SaveStatus.Saved -> Colors.SuccessLight
                    is SaveStatus.Error -> Colors.ErrorLight
                    else -> Color("transparent")
                }
            )
        }
    }) {
        // Status icon
        icon?.let {
            Span({
                style {
                    fontSize(14.px)
                    color(color)
                    if (state.status is SaveStatus.Saving) {
                        property("animation", "spin 1s linear infinite")
                    }
                }
            }) {
                Text(it)
            }
        }

        // Status text
        Span({
            style {
                fontSize(12.px)
                fontWeight(500)
                color(color)
            }
        }) {
            Text(text)
        }

        // Timestamp (only for saved state, non-compact)
        if (!compact && state.status is SaveStatus.Saved && state.lastSaveTime != null) {
            Span({
                style {
                    fontSize(11.px)
                    color(Colors.TextSecondary)
                    marginLeft(4.px)
                }
            }) {
                // Update relative time periodically
                var relativeTime by remember { mutableStateOf(formatRelativeTime(state.lastSaveTime)) }

                LaunchedEffect(state.lastSaveTime) {
                    while (true) {
                        kotlinx.coroutines.delay(10000) // Update every 10 seconds
                        relativeTime = formatRelativeTime(state.lastSaveTime)
                    }
                }

                Text("($relativeTime)")
            }
        }

        // Retry info for errors
        if (state.status is SaveStatus.Error && state.status.retryCount > 0) {
            Span({
                style {
                    fontSize(11.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("(retry ${state.status.retryCount})")
            }
        }
    }
}

/**
 * A more detailed save status panel for debugging or verbose mode.
 */
@Composable
fun SaveStatusPanel(
    state: AutoSaveState,
    onRetry: (() -> Unit)? = null
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(8.px)
            padding(12.px, 16.px)
            property("box-shadow", "0 2px 4px rgba(0, 0, 0, 0.1)")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
            }
        }) {
            // Status indicator
            SaveStatusIndicator(state = state, compact = false)

            // Manual retry button (only show on error)
            if (state.status is SaveStatus.Error && onRetry != null) {
                Button({
                    style {
                        backgroundColor(Colors.Primary)
                        color(Colors.White)
                        border(0.px)
                        borderRadius(4.px)
                        padding(6.px, 12.px)
                        fontSize(12.px)
                        cursor("pointer")
                    }
                    onClick { onRetry() }
                }) {
                    Text("Retry")
                }
            }
        }

        // Pending changes indicator
        if (state.pendingChanges) {
            Div({
                style {
                    marginTop(8.px)
                    fontSize(11.px)
                    color(Colors.Warning)
                }
            }) {
                Text("• Unsaved changes")
            }
        }
    }
}

/**
 * Inline save status for header integration.
 */
@Composable
fun InlineSaveStatus(state: AutoSaveState) {
    when (val status = state.status) {
        is SaveStatus.Idle -> {
            // Show nothing or subtle indicator
        }
        is SaveStatus.Saving -> {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                }
            }) {
                // Spinning indicator
                Div({
                    style {
                        property("width", "12px")
                        property("height", "12px")
                        border(2.px, LineStyle.Solid, Colors.White)
                        borderRadius(50.percent)
                        property("border-top-color", "transparent")
                        property("animation", "spin 0.8s linear infinite")
                    }
                })
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.White)
                        opacity(0.9)
                    }
                }) {
                    Text("Saving...")
                }
            }
        }
        is SaveStatus.Saved -> {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                    backgroundColor(rgba(255, 255, 255, 0.2))
                    borderRadius(4.px)
                    padding(4.px, 8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.White)
                    }
                }) {
                    Text("✓ Saved")
                }
            }
        }
        is SaveStatus.Error -> {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                    backgroundColor(Colors.Error)
                    borderRadius(4.px)
                    padding(4.px, 8.px)
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.White)
                    }
                }) {
                    Text("⚠ ${status.message}")
                }
            }
        }
    }
}
